import type { CSSProperties } from 'react';
import { z } from 'zod';
import * as Lucide from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTextProp, useProp } from '@docgen/sdk';
import type { BlockDef } from '../types.js';

export const imageBlock: BlockDef = {
  type: 'image',
  name: 'Image',
  description: 'An image from a URL; optional circle crop.',
  category: 'media',
  icon: 'image',
  propsSchema: z.object({
    src: z.string(),
    alt: z.string().default(''),
    width: z.string().default('100%'),
    height: z.string().optional(),
    shape: z.enum(['default', 'circle']).default('default'),
  }),
  propFields: [
    { kind: 'image', name: 'src', label: 'Image' },
    { kind: 'text', name: 'alt', label: 'Alt text', default: '' },
    { kind: 'text', name: 'width', label: 'Width (CSS)', default: '100%' },
    { kind: 'text', name: 'height', label: 'Height (CSS)' },
    { kind: 'select', name: 'shape', label: 'Shape', options: ['default', 'circle'], default: 'default' },
  ],
  styleFields: ['radius', 'mt', 'mb'],
  defaults: { props: { src: '', alt: '', width: '100%', shape: 'default' } },
  Component: function Image({ attrs, style }) {
    const src = useTextProp('src');
    const alt = useTextProp('alt');
    const width = useTextProp('width') || '100%';
    const height = useProp('height');
    const circle = useProp('shape') === 'circle';
    if (!src) {
      return (
        <div
          {...attrs}
          style={{
            width, height: typeof height === 'string' && height ? height : 80,
            background: 'var(--mist)', border: '1px dashed var(--line)', borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--blue)', fontSize: '9pt', ...style,
          }}
        >
          image
        </div>
      );
    }
    const heightStr = typeof height === 'string' && height !== '' ? height : undefined;
    // A circle crops to a square. Otherwise `height` caps how tall the image can
    // get (max-height). Pages lay blocks out in a flex column, which stretches
    // an auto-width image to the full column width while max-height holds its
    // height, so without object-fit the photo is squashed (every program photo
    // on a proposal, Oct 2026). `cover` fills that box and crops the overflow;
    // the box itself is unchanged, so pagination does not move.
    const sizing: CSSProperties = circle
      ? { width, height: heightStr, borderRadius: '50%', objectFit: 'cover' }
      : heightStr
        ? { width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: heightStr, objectFit: 'cover' }
        : { width };
    return <img {...attrs} src={src} alt={alt} style={{ display: 'block', ...sizing, ...style }} />;
  },
};

/** Turns a lucide icon name ("arrow-right", "Trophy", "users") into its component, or null. */
function lucideIcon(name: string): LucideIcon | null {
  const pascal = name
    .trim()
    .replace(/[-_\s]+(.)/g, (_, c: string) => c.toUpperCase())
    .replace(/^(.)/, (_, c: string) => c.toUpperCase());
  const found = (Lucide as unknown as Record<string, unknown>)[pascal];
  return typeof found === 'object' || typeof found === 'function' ? (found as LucideIcon) : null;
}

/** Circular badge fills — brand tokens; the icon color is picked for contrast. */
const ICON_BADGES = ['none', 'yellow', 'navy', 'royal', 'sky', 'mist'] as const;

export const iconBlock: BlockDef = {
  type: 'icon',
  name: 'Icon',
  description:
    'A lucide icon by name (e.g. "trophy", "users", "calendar", "check", "heart-handshake", "map-pin"). `badge` wraps it in a round brand-colored badge (the standard look for a card/feature icon — yellow by default); "none" is a bare glyph. Size in pixels; color a bare glyph with the color style.',
  category: 'media',
  icon: 'sparkles',
  propsSchema: z.object({
    name: z.string().default('star'),
    size: z.number().default(22),
    badge: z.enum(ICON_BADGES).default('yellow'),
  }),
  propFields: [
    { kind: 'text', name: 'name', label: 'Icon name (lucide)', default: 'star' },
    { kind: 'number', name: 'size', label: 'Size (px)', default: 22 },
    { kind: 'select', name: 'badge', label: 'Badge', options: [...ICON_BADGES], default: 'yellow' },
  ],
  styleFields: ['color', 'mt', 'mb'],
  defaults: { props: { name: 'star', size: 22, badge: 'yellow' } },
  Component: function Icon({ attrs, style }) {
    const name = useTextProp('name') || 'star';
    const size = Number(useProp('size') ?? 22);
    const badge = String(useProp('badge') ?? 'yellow');
    const Cmp = lucideIcon(name);
    const glyph = Cmp ? <Cmp size={size} aria-hidden focusable={false} /> : null;
    // A round brand badge — the default look for a card/feature icon. The badge
    // class sets a contrasting icon color; a `color` style still overrides it.
    if (badge !== 'none' && (ICON_BADGES as readonly string[]).includes(badge)) {
      return (
        <span {...attrs} className={`icon-badge icon-badge--${badge}`} style={style}>
          {glyph}
        </span>
      );
    }
    if (!glyph) return <span {...attrs} style={{ display: 'inline-block', width: size, height: size, ...style }} aria-hidden />;
    return (
      <span {...attrs} style={{ display: 'inline-flex', lineHeight: 0, ...style }}>
        {glyph}
      </span>
    );
  },
};

export const logoBlock: BlockDef = {
  type: 'logo',
  name: 'Logo',
  description: 'The brand logo at a fixed width.',
  category: 'media',
  icon: 'badge-check',
  propsSchema: z.object({
    src: z.string(),
    alt: z.string().default(''),
    width: z.number().default(190),
  }),
  propFields: [
    { kind: 'image', name: 'src', label: 'Logo URL' },
    { kind: 'text', name: 'alt', label: 'Alt text', default: '' },
    { kind: 'number', name: 'width', label: 'Width (px)', default: 190 },
  ],
  styleFields: ['mt', 'mb'],
  defaults: { props: { src: '', alt: 'Logo', width: 190 } },
  Component: function Logo({ attrs, style }) {
    const src = useTextProp('src');
    const alt = useTextProp('alt');
    const width = Number(useProp('width') ?? 190);
    if (!src) {
      return (
        <div {...attrs} style={{ width, height: 48, background: 'var(--mist)', borderRadius: 8, ...style }} />
      );
    }
    return <img {...attrs} src={src} alt={alt} style={{ width, ...style }} />;
  },
};
